import React, { useRef, useState, useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { Button } from "devextreme-react/button";
import ContextMenu from "devextreme-react/context-menu";
import Popup from "devextreme-react/popup";
import Report from "../../../../../reports/components/Report";
import { recordNumberFilter } from "../../../../../reports/components/reportFilters";
import routes from "../../../../../assets/apiRoutes.json";
import { MESSAGES } from "../../../../../common/Messages";
import { getReportMenu } from "../../../../../services/ReportGroupService";

/**
 * Print button + dropdown of the reports a screen offers, each opened in a popup already scoped to the
 * selected record.
 *
 * Two shapes of menu:
 *  - default: every runnable report in the module's DB report groups, gated on the role's OMReports
 *    permissions. This is the Sales Order Management / Data Entry toolbar button.
 *  - `items`: a fixed list, for a screen that offers named reports rather than the whole module - e.g.
 *    a dialog migrated from a Delphi form whose own TAbReportObjects were its Print popup. Static
 *    entries go through the same OMReports gate, so naming a report here does not hand it to a role
 *    that may not view it.
 *
 * `embedded` hosts the report inside a dialog: the popup renders into #root, the criteria and Available
 * Fields panes are sized from `paneHeight` instead of the viewport, and the widgets are re-measured
 * once the popup has finished animating.
 */

// The OM report groups come from the DB (same source as the sidebar). Cached so
// multiple Print buttons share one call.
let cachedMenuPromise = null;
function fetchOmReportMenu(accessToken) {
  if (!cachedMenuPromise) {
    cachedMenuPromise = getReportMenu("OM", accessToken).catch((err) => {
      cachedMenuPromise = null;
      console.log(err, "Error getting OM report menu for print menu");
      return [];
    });
  }
  return cachedMenuPromise;
}

// Role permissions (OMReports) - cached across Print buttons.
let cachedPermissionsPromise = null;
function fetchOmReportPermissions(role, accessToken) {
  if (!cachedPermissionsPromise) {
    cachedPermissionsPromise = axios
      .get(`${window._env_.REACT_APP_AUTH_URL}/${routes.idp.GetPermissionsByRole}?roleName=${role}`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => res.data)
      .catch((err) => {
        cachedPermissionsPromise = null;
        console.log(err, "Error getting role permissions for print menu");
        return null;
      });
  }
  return cachedPermissionsPromise;
}

// The popup takes 90% of the window in both directions (a DevExtreme percentage is of the window, so
// the height is also 90vh); the panes inside it get that height less the report's own chrome (format
// bar, macro row, tab header). One number drives both so they cannot drift apart. Only the embedded
// layout needs it - a full-page report sizes its panes from the viewport.
const REPORT_POPUP_PERCENT = 90;
const REPORT_POPUP_SIZE = `${REPORT_POPUP_PERCENT}%`;
const REPORT_POPUP_CHROME_PX = 300;
const EMBEDDED_PANE_HEIGHT = `calc(${REPORT_POPUP_PERCENT}vh - ${REPORT_POPUP_CHROME_PX}px)`;
// A popup hosted inside another dialog has to render into the app root, not into the dialog.
const EMBEDDED_POPUP_CONTAINER = "#root";
// DevExtreme measures a widget when it is created, which inside a still-animating popup is stale.
const EMBEDDED_REMEASURE_DELAY_MS = 100;

// A report with no permission of its own is open to everyone; one that names a permission needs the
// role to carry it. Until the role's permissions have loaded, omReports is null and nothing gated
// shows - the menu fills in rather than briefly offering reports the role cannot view.
const isReportAllowed = (permission, omReports) =>
  !permission || (!!omReports && omReports.includes(permission));

// Build the ContextMenu data source from a caller-supplied list. Same gate as the DB items, so a
// screen naming its own reports does not bypass OMReports.
function buildStaticMenuItems(items, omReports) {
  return (items || [])
    .filter((item) => item && item.report && isReportAllowed(item.permission, omReports))
    .map((item) => ({
      text: item.text || item.report.title || item.report.name,
      report: item.report,
    }));
}

// Build the ContextMenu data source from the DB groups, gated by permission.
// Only compiled reports (ABRUNPROGRAM) are runnable per-order from here.
function buildMenuItems(menu, omReports) {
  const allowed = (p) => isReportAllowed(p, omReports);
  const toLeaf = (r) => ({
    text: r.Description || r.Name || r.FormName || r.ReportKey,
    report: {
      name: r.Name || r.FormName || r.ReportKey, // load the report by its Name
      fullName: r.Parameter,
      title: r.Description || r.Name || r.FormName || r.ReportKey,
    },
  });
  const result = [];
  (menu || []).forEach((group, gi) => {
    // Permission gating is per report item only (groups carry no permission).
    const reports = (group.ReportGroupItems || []).filter((r) => r.SourceTableName !== "ABLAYOUT" && allowed(r.Permission));
    if (reports.length === 0) return;
    if (!group.GroupName) {
      reports.forEach((r) => result.push(toLeaf(r)));
    } else {
      result.push({ key: `g_${gi}`, text: group.GroupName, items: reports.map(toLeaf) });
    }
  });
  return result;
}

let printMenuCounter = 0;

const PrintReportMenu = (props) => {
  const {
    selectedRecord,
    recordNumberField = "recordNumber",
    items,
    embedded = false,
    paneHeight = EMBEDDED_PANE_HEIGHT,
    text = MESSAGES.PRINT,
    className = "gridTopButton",
    disabled = false,
  } = props;
  const hasStaticItems = Array.isArray(items);

  const buttonIdRef = useRef(`omPrintReportBtn_${++printMenuCounter}`);
  const contextMenuRef = useRef(null);
  const [activeReport, setActiveReport] = useState(null);
  const [reportMenu, setReportMenu] = useState([]);
  const [permissions, setPermissions] = useState(null);

  const role = props.oidc?.user?.profile?.role;
  const accessToken = props.oidc?.user?.access_token;

  useEffect(() => {
    let mounted = true;
    // A caller-supplied list needs no report groups, only the permissions that gate it.
    if (!hasStaticItems) {
      fetchOmReportMenu(accessToken).then((data) => {
        if (mounted) setReportMenu(Array.isArray(data) ? data : []);
      });
    }
    fetchOmReportPermissions(role, accessToken).then((data) => {
      if (mounted) setPermissions(data);
    });
    return () => {
      mounted = false;
    };
  }, [role, accessToken, hasStaticItems]);

  const menuItems = useMemo(
    () =>
      hasStaticItems
        ? buildStaticMenuItems(items, permissions?.OMReports)
        : buildMenuItems(reportMenu, permissions?.OMReports),
    [hasStaticItems, items, reportMenu, permissions]
  );

  const recordNumber =
    selectedRecord && recordNumberField ? selectedRecord[recordNumberField] : null;

  const showMenu = useCallback(() => {
    if (contextMenuRef.current) {
      contextMenuRef.current.instance().show();
    }
  }, []);

  const onItemClick = useCallback((e) => {
    const itemData = e.itemData;
    if (itemData && itemData.report) {
      setActiveReport(itemData.report);
    }
  }, []);

  const closeReport = useCallback(() => setActiveReport(null), []);

  // Inside a dialog the report's widgets are built while the popup is still animating to its final
  // size, so they measure the wrong height - which is what leaves the Available Fields drawer looking
  // dead. One resize once the animation is over is enough; wm ReportViewerPopUp nudges it the same way.
  useEffect(() => {
    if (!embedded || !activeReport) return undefined;
    const timer = setTimeout(() => window.dispatchEvent(new Event("resize")), EMBEDDED_REMEASURE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [embedded, activeReport]);

  const initialFilters = recordNumberFilter(recordNumber);

  const renderReport = () => {
    const report = (
      <Report
        key={activeReport.name}
        Name={activeReport.name}
        FullName={activeReport.fullName}
        Title={activeReport.title}
        initialFilters={initialFilters}
        embedded={embedded}
        paneHeight={embedded ? paneHeight : undefined}
      />
    );
    // A column with a definite height, the way the Select Items range dialog hosts its own report
    // panel: `embedded` pins the Available Fields list beside the criteria grid and sizes both from
    // paneHeight, and neither can do that inside a box whose height is only implied.
    return embedded ? (
      <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        <div style={{ flex: "1 1 auto", minHeight: 0, overflow: "auto" }}>{report}</div>
      </div>
    ) : (
      report
    );
  };

  return (
    <React.Fragment>
      <Button
        id={buttonIdRef.current}
        className={className}
        text={text}
        type="default"
        disabled={disabled}
        onClick={showMenu}
      />
      <ContextMenu
        ref={contextMenuRef}
        dataSource={menuItems}
        target={`#${buttonIdRef.current}`}
        showEvent=""
        position={{ my: "top left", at: "bottom left", of: `#${buttonIdRef.current}` }}
        onItemClick={onItemClick}
      />
      {activeReport && (
        <Popup
          visible={true}
          onHiding={closeReport}
          title={activeReport.title}
          showTitle={true}
          showCloseButton={true}
          container={embedded ? EMBEDDED_POPUP_CONTAINER : undefined}
          width={REPORT_POPUP_SIZE}
          height={REPORT_POPUP_SIZE}
          dragEnabled={true}
          resizeEnabled={true}
          contentRender={renderReport}
        />
      )}
    </React.Fragment>
  );
};

function mapStateToProps(state) {
  return {
    oidc: state.oidc,
  };
}

export default connect(mapStateToProps)(PrintReportMenu);
